/**
 * Agent Email Service
 * Handles email operations for AI agents including sending, scheduling, and tracking
 */

import { AgentAPIRequest, AgentAPIResponse } from './AgentAPIGateway';

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  category: 'promotional' | 'transactional' | 'newsletter' | 'welcome' | 'sequence';
  variables: string[];
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
}

export interface EmailRecipient {
  email: string;
  name?: string;
  variables?: Record<string, any>;
}

export interface EmailSendRequest {
  templateId?: string;
  subject?: string;
  htmlContent?: string;
  textContent?: string;
  recipients: EmailRecipient[];
  fromEmail?: string;
  fromName?: string;
  replyTo?: string;
  tags?: string[];
  trackOpens?: boolean;
  trackClicks?: boolean;
  scheduledAt?: Date;
}

export interface BulkEmailRequest {
  templateId?: string;
  subject?: string;
  htmlContent?: string;
  textContent?: string;
  recipients: EmailRecipient[];
  fromEmail?: string;
  fromName?: string;
  replyTo?: string;
  tags?: string[];
  trackOpens?: boolean;
  trackClicks?: boolean;
  batchSize?: number;
  scheduledAt?: Date;
}

export interface EmailSequenceRequest {
  sequenceId: string;
  recipients: EmailRecipient[];
  startDelay?: number; // minutes
  tags?: string[];
}

export interface EmailSequence {
  id: string;
  name: string;
  description: string;
  emails: Array<{
    id: string;
    templateId: string;
    delayMinutes: number;
    conditions?: Record<string, any>;
  }>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface EmailDeliveryStatus {
  id: string;
  status: 'queued' | 'sending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | 'unsubscribed';
  recipient: string;
  subject: string;
  sentAt?: Date;
  deliveredAt?: Date;
  openedAt?: Date;
  clickedAt?: Date;
  bouncedAt?: Date;
  failedAt?: Date;
  errorMessage?: string;
  trackingData?: {
    opens: number;
    clicks: number;
    lastOpened?: Date;
    lastClicked?: Date;
    userAgent?: string;
    ipAddress?: string;
  };
}

export interface EmailValidationError {
  field: string;
  message: string;
  code: string;
}

export interface EmailPermissions {
  canSend: boolean;
  canSchedule: boolean;
  canAccessTemplates: boolean;
  canTrackDelivery: boolean;
  canSendBulk: boolean;
  canTriggerSequences: boolean;
  maxRecipientsPerEmail: number;
  maxEmailsPerHour: number;
  maxEmailsPerDay: number;
}

export class AgentEmailService {
  private emailTemplates: Map<string, EmailTemplate> = new Map();
  private emailSequences: Map<string, EmailSequence> = new Map();
  private emailDeliveries: Map<string, EmailDeliveryStatus> = new Map();
  private scheduledEmails: Array<{
    id: string;
    request: EmailSendRequest | BulkEmailRequest;
    scheduledAt: Date;
    agentId: string;
  }> = [];

  constructor() {
    this.initializeMockData();
  }

  /**
   * Send single email
   */
  async sendEmail(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canSend) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to send emails', 403, startTime);
      }

      // Validate request data
      const validationErrors = this.validateEmailSendRequest(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Email send request validation failed', 400, startTime, { errors: validationErrors });
      }

      const emailRequest: EmailSendRequest = request.body;

      // Check recipient limits
      if (emailRequest.recipients.length > permissions.maxRecipientsPerEmail) {
        return this.createErrorResponse(request, 'RECIPIENT_LIMIT_EXCEEDED', `Maximum ${permissions.maxRecipientsPerEmail} recipients allowed per email`, 400, startTime);
      }

      // Validate content policy
      const policyViolations = await this.validateEmailContent(emailRequest);
      if (policyViolations.length > 0) {
        return this.createErrorResponse(request, 'CONTENT_POLICY_VIOLATION', 'Email content violates policy', 400, startTime, { violations: policyViolations });
      }

      // Process email sending
      const emailId = this.generateEmailId();
      const deliveryStatuses: EmailDeliveryStatus[] = [];

      for (const recipient of emailRequest.recipients) {
        const deliveryId = `${emailId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const deliveryStatus: EmailDeliveryStatus = {
          id: deliveryId,
          status: emailRequest.scheduledAt ? 'queued' : 'sending',
          recipient: recipient.email,
          subject: emailRequest.subject || 'No Subject',
          sentAt: emailRequest.scheduledAt ? undefined : new Date(),
          trackingData: {
            opens: 0,
            clicks: 0
          }
        };

        this.emailDeliveries.set(deliveryId, deliveryStatus);
        deliveryStatuses.push(deliveryStatus);

        // Simulate delivery process
        if (!emailRequest.scheduledAt) {
          setTimeout(() => this.simulateEmailDelivery(deliveryId), Math.random() * 5000);
        }
      }

      // Handle scheduling
      if (emailRequest.scheduledAt) {
        this.scheduledEmails.push({
          id: emailId,
          request: emailRequest,
          scheduledAt: emailRequest.scheduledAt,
          agentId: request.agentId
        });
      }

      return this.createSuccessResponse(request, {
        emailId,
        status: emailRequest.scheduledAt ? 'scheduled' : 'sending',
        recipients: emailRequest.recipients.length,
        scheduledAt: emailRequest.scheduledAt,
        deliveryIds: deliveryStatuses.map(d => d.id),
        estimatedDeliveryTime: emailRequest.scheduledAt || new Date(Date.now() + 60000)
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Send bulk email
   */
  async sendBulkEmail(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canSendBulk) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to send bulk emails', 403, startTime);
      }

      // Validate request data
      const validationErrors = this.validateBulkEmailRequest(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Bulk email request validation failed', 400, startTime, { errors: validationErrors });
      }

      const bulkRequest: BulkEmailRequest = request.body;

      // Check recipient limits
      if (bulkRequest.recipients.length > permissions.maxRecipientsPerEmail * 10) {
        return this.createErrorResponse(request, 'BULK_RECIPIENT_LIMIT_EXCEEDED', `Maximum ${permissions.maxRecipientsPerEmail * 10} recipients allowed per bulk email`, 400, startTime);
      }

      // Validate content policy
      const policyViolations = await this.validateEmailContent(bulkRequest);
      if (policyViolations.length > 0) {
        return this.createErrorResponse(request, 'CONTENT_POLICY_VIOLATION', 'Email content violates policy', 400, startTime, { violations: policyViolations });
      }

      // Process bulk email
      const emailId = this.generateEmailId();
      const batchSize = bulkRequest.batchSize || 100;
      const batches = this.chunkArray(bulkRequest.recipients, batchSize);
      const deliveryStatuses: EmailDeliveryStatus[] = [];

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        for (const recipient of batch) {
          const deliveryId = `${emailId}_batch${batchIndex}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const deliveryStatus: EmailDeliveryStatus = {
            id: deliveryId,
            status: bulkRequest.scheduledAt ? 'queued' : 'sending',
            recipient: recipient.email,
            subject: bulkRequest.subject || 'No Subject',
            sentAt: bulkRequest.scheduledAt ? undefined : new Date(Date.now() + batchIndex * 60000), // Stagger batches
            trackingData: {
              opens: 0,
              clicks: 0
            }
          };

          this.emailDeliveries.set(deliveryId, deliveryStatus);
          deliveryStatuses.push(deliveryStatus);

          // Simulate delivery process with batch delays
          if (!bulkRequest.scheduledAt) {
            setTimeout(() => this.simulateEmailDelivery(deliveryId), (batchIndex * 60000) + (Math.random() * 30000));
          }
        }
      }

      // Handle scheduling
      if (bulkRequest.scheduledAt) {
        this.scheduledEmails.push({
          id: emailId,
          request: bulkRequest,
          scheduledAt: bulkRequest.scheduledAt,
          agentId: request.agentId
        });
      }

      return this.createSuccessResponse(request, {
        emailId,
        status: bulkRequest.scheduledAt ? 'scheduled' : 'sending',
        recipients: bulkRequest.recipients.length,
        batches: batches.length,
        batchSize,
        scheduledAt: bulkRequest.scheduledAt,
        deliveryIds: deliveryStatuses.map(d => d.id),
        estimatedCompletionTime: bulkRequest.scheduledAt || new Date(Date.now() + (batches.length * 60000))
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Trigger email sequence
   */
  async triggerEmailSequence(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canTriggerSequences) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to trigger email sequences', 403, startTime);
      }

      // Validate request data
      const validationErrors = this.validateSequenceRequest(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Email sequence request validation failed', 400, startTime, { errors: validationErrors });
      }

      const sequenceRequest: EmailSequenceRequest = request.body;

      // Get sequence
      const sequence = this.emailSequences.get(sequenceRequest.sequenceId);
      if (!sequence) {
        return this.createErrorResponse(request, 'SEQUENCE_NOT_FOUND', `Email sequence ${sequenceRequest.sequenceId} not found`, 404, startTime);
      }

      if (!sequence.isActive) {
        return this.createErrorResponse(request, 'SEQUENCE_INACTIVE', 'Email sequence is not active', 400, startTime);
      }

      // Process sequence trigger
      const sequenceInstanceId = this.generateSequenceInstanceId();
      const startDelay = sequenceRequest.startDelay || 0;

      // Schedule each email in the sequence
      const scheduledEmails = [];
      for (const sequenceEmail of sequence.emails) {
        const scheduledAt = new Date(Date.now() + startDelay * 60000 + sequenceEmail.delayMinutes * 60000);
        
        const emailRequest: EmailSendRequest = {
          templateId: sequenceEmail.templateId,
          recipients: sequenceRequest.recipients,
          tags: [...(sequenceRequest.tags || []), 'sequence', sequence.name],
          trackOpens: true,
          trackClicks: true,
          scheduledAt
        };

        this.scheduledEmails.push({
          id: `${sequenceInstanceId}_${sequenceEmail.id}`,
          request: emailRequest,
          scheduledAt,
          agentId: request.agentId
        });

        scheduledEmails.push({
          sequenceEmailId: sequenceEmail.id,
          templateId: sequenceEmail.templateId,
          scheduledAt,
          recipients: sequenceRequest.recipients.length
        });
      }

      return this.createSuccessResponse(request, {
        sequenceInstanceId,
        sequenceId: sequenceRequest.sequenceId,
        sequenceName: sequence.name,
        recipients: sequenceRequest.recipients.length,
        emailsScheduled: scheduledEmails.length,
        startDelay: startDelay,
        scheduledEmails,
        estimatedCompletionTime: new Date(Date.now() + startDelay * 60000 + Math.max(...sequence.emails.map(e => e.delayMinutes)) * 60000)
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Schedule email for future delivery
   */
  async scheduleEmail(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canSchedule) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to schedule emails', 403, startTime);
      }

      // Validate request data
      const validationErrors = this.validateEmailSendRequest(request.body);
      if (validationErrors.length > 0) {
        return this.createErrorResponse(request, 'VALIDATION_ERROR', 'Email schedule request validation failed', 400, startTime, { errors: validationErrors });
      }

      const emailRequest: EmailSendRequest = request.body;

      if (!emailRequest.scheduledAt) {
        return this.createErrorResponse(request, 'MISSING_SCHEDULE_TIME', 'Scheduled time is required for email scheduling', 400, startTime);
      }

      const scheduledAt = new Date(emailRequest.scheduledAt);
      if (scheduledAt <= new Date()) {
        return this.createErrorResponse(request, 'INVALID_SCHEDULE_TIME', 'Scheduled time must be in the future', 400, startTime);
      }

      // Validate content policy
      const policyViolations = await this.validateEmailContent(emailRequest);
      if (policyViolations.length > 0) {
        return this.createErrorResponse(request, 'CONTENT_POLICY_VIOLATION', 'Email content violates policy', 400, startTime, { violations: policyViolations });
      }

      // Schedule email
      const emailId = this.generateEmailId();
      this.scheduledEmails.push({
        id: emailId,
        request: emailRequest,
        scheduledAt,
        agentId: request.agentId
      });

      return this.createSuccessResponse(request, {
        emailId,
        status: 'scheduled',
        scheduledAt,
        recipients: emailRequest.recipients.length,
        subject: emailRequest.subject
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Get email delivery status
   */
  async getEmailStatus(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canTrackDelivery) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to track email delivery', 403, startTime);
      }

      const emailId = request.parameters.id;
      if (!emailId) {
        return this.createErrorResponse(request, 'MISSING_PARAMETER', 'Email ID is required', 400, startTime);
      }

      // Find delivery statuses for this email
      const deliveryStatuses = Array.from(this.emailDeliveries.values())
        .filter(delivery => delivery.id.startsWith(emailId));

      if (deliveryStatuses.length === 0) {
        return this.createErrorResponse(request, 'EMAIL_NOT_FOUND', `Email with ID ${emailId} not found`, 404, startTime);
      }

      // Calculate summary statistics
      const summary = {
        emailId,
        totalRecipients: deliveryStatuses.length,
        sent: deliveryStatuses.filter(d => ['sent', 'delivered', 'opened', 'clicked'].includes(d.status)).length,
        delivered: deliveryStatuses.filter(d => ['delivered', 'opened', 'clicked'].includes(d.status)).length,
        opened: deliveryStatuses.filter(d => ['opened', 'clicked'].includes(d.status)).length,
        clicked: deliveryStatuses.filter(d => d.status === 'clicked').length,
        bounced: deliveryStatuses.filter(d => d.status === 'bounced').length,
        failed: deliveryStatuses.filter(d => d.status === 'failed').length,
        unsubscribed: deliveryStatuses.filter(d => d.status === 'unsubscribed').length,
        deliveryRate: 0,
        openRate: 0,
        clickRate: 0,
        bounceRate: 0
      };

      // Calculate rates
      if (summary.sent > 0) {
        summary.deliveryRate = (summary.delivered / summary.sent) * 100;
        summary.openRate = (summary.opened / summary.sent) * 100;
        summary.clickRate = (summary.clicked / summary.sent) * 100;
        summary.bounceRate = (summary.bounced / summary.sent) * 100;
      }

      return this.createSuccessResponse(request, {
        summary,
        deliveries: deliveryStatuses
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  /**
   * Get email templates
   */
  async getEmailTemplates(request: AgentAPIRequest): Promise<AgentAPIResponse> {
    const startTime = Date.now();

    try {
      // Validate permissions
      const permissions = await this.getAgentPermissions(request.agentId);
      if (!permissions.canAccessTemplates) {
        return this.createErrorResponse(request, 'INSUFFICIENT_PERMISSIONS', 'Agent does not have permission to access email templates', 403, startTime);
      }

      const { category, active, limit = 50, offset = 0 } = request.parameters;
      
      let templates = Array.from(this.emailTemplates.values());

      // Apply filters
      if (category) {
        templates = templates.filter(t => t.category === category);
      }
      if (active !== undefined) {
        const isActive = active === 'true';
        templates = templates.filter(t => t.isActive === isActive);
      }

      // Apply pagination
      const total = templates.length;
      const paginatedTemplates = templates.slice(offset, offset + limit);

      return this.createSuccessResponse(request, {
        templates: paginatedTemplates,
        pagination: {
          total,
          limit,
          offset,
          hasMore: offset + limit < total
        }
      }, 200, startTime);

    } catch (error) {
      return this.createErrorResponse(request, 'INTERNAL_ERROR', error instanceof Error ? error.message : 'Internal server error', 500, startTime);
    }
  }

  // Private helper methods

  private async getAgentPermissions(_agentId: string): Promise<EmailPermissions> {
    // In a real implementation, this would fetch from the agent registration service
    // For now, return full permissions for all agents
    return {
      canSend: true,
      canSchedule: true,
      canAccessTemplates: true,
      canTrackDelivery: true,
      canSendBulk: true,
      canTriggerSequences: true,
      maxRecipientsPerEmail: 1000,
      maxEmailsPerHour: 100,
      maxEmailsPerDay: 1000
    };
  }

  private validateEmailSendRequest(data: any): EmailValidationError[] {
    const errors: EmailValidationError[] = [];

    if (!data.recipients || !Array.isArray(data.recipients) || data.recipients.length === 0) {
      errors.push({ field: 'recipients', message: 'At least one recipient is required', code: 'REQUIRED' });
    } else {
      data.recipients.forEach((recipient: any, index: number) => {
        if (!recipient.email || typeof recipient.email !== 'string') {
          errors.push({ field: `recipients[${index}].email`, message: 'Valid email address is required', code: 'INVALID_EMAIL' });
        } else if (!this.isValidEmail(recipient.email)) {
          errors.push({ field: `recipients[${index}].email`, message: 'Invalid email format', code: 'INVALID_FORMAT' });
        }
      });
    }

    if (!data.templateId && !data.subject) {
      errors.push({ field: 'subject', message: 'Subject is required when not using a template', code: 'REQUIRED' });
    }

    if (!data.templateId && !data.htmlContent && !data.textContent) {
      errors.push({ field: 'content', message: 'HTML or text content is required when not using a template', code: 'REQUIRED' });
    }

    if (data.scheduledAt && isNaN(Date.parse(data.scheduledAt))) {
      errors.push({ field: 'scheduledAt', message: 'Invalid scheduled date format', code: 'INVALID_FORMAT' });
    }

    return errors;
  }

  private validateBulkEmailRequest(data: any): EmailValidationError[] {
    const errors = this.validateEmailSendRequest(data);

    if (data.batchSize && (typeof data.batchSize !== 'number' || data.batchSize < 1 || data.batchSize > 1000)) {
      errors.push({ field: 'batchSize', message: 'Batch size must be between 1 and 1000', code: 'INVALID_VALUE' });
    }

    return errors;
  }

  private validateSequenceRequest(data: any): EmailValidationError[] {
    const errors: EmailValidationError[] = [];

    if (!data.sequenceId || typeof data.sequenceId !== 'string') {
      errors.push({ field: 'sequenceId', message: 'Sequence ID is required', code: 'REQUIRED' });
    }

    if (!data.recipients || !Array.isArray(data.recipients) || data.recipients.length === 0) {
      errors.push({ field: 'recipients', message: 'At least one recipient is required', code: 'REQUIRED' });
    } else {
      data.recipients.forEach((recipient: any, index: number) => {
        if (!recipient.email || !this.isValidEmail(recipient.email)) {
          errors.push({ field: `recipients[${index}].email`, message: 'Valid email address is required', code: 'INVALID_EMAIL' });
        }
      });
    }

    if (data.startDelay && (typeof data.startDelay !== 'number' || data.startDelay < 0)) {
      errors.push({ field: 'startDelay', message: 'Start delay must be a non-negative number', code: 'INVALID_VALUE' });
    }

    return errors;
  }

  private async validateEmailContent(emailRequest: EmailSendRequest | BulkEmailRequest): Promise<string[]> {
    const violations: string[] = [];

    // Check for spam keywords
    const spamKeywords = ['free', 'urgent', 'act now', 'limited time', 'click here'];
    const content = `${emailRequest.subject || ''} ${emailRequest.htmlContent || ''} ${emailRequest.textContent || ''}`.toLowerCase();
    
    spamKeywords.forEach(keyword => {
      if (content.includes(keyword)) {
        violations.push(`Contains potential spam keyword: "${keyword}"`);
      }
    });

    // Check for excessive capitalization
    if (emailRequest.subject && emailRequest.subject.length > 10) {
      const capsRatio = (emailRequest.subject.match(/[A-Z]/g) || []).length / emailRequest.subject.length;
      if (capsRatio > 0.5) {
        violations.push('Subject contains excessive capitalization');
      }
    }

    return violations;
  }

  private simulateEmailDelivery(deliveryId: string): void {
    const delivery = this.emailDeliveries.get(deliveryId);
    if (!delivery) return;

    // Simulate delivery progression
    setTimeout(() => {
      delivery.status = 'delivered';
      delivery.deliveredAt = new Date();
      
      // Simulate opens (30% chance)
      if (Math.random() < 0.3) {
        setTimeout(() => {
          delivery.status = 'opened';
          delivery.openedAt = new Date();
          delivery.trackingData!.opens = 1;
          delivery.trackingData!.lastOpened = new Date();
          
          // Simulate clicks (20% of opens)
          if (Math.random() < 0.2) {
            setTimeout(() => {
              delivery.status = 'clicked';
              delivery.clickedAt = new Date();
              delivery.trackingData!.clicks = 1;
              delivery.trackingData!.lastClicked = new Date();
            }, Math.random() * 300000); // 0-5 minutes after open
          }
        }, Math.random() * 3600000); // 0-1 hour after delivery
      }
      
      // Simulate bounces (2% chance)
      if (Math.random() < 0.02) {
        delivery.status = 'bounced';
        delivery.bouncedAt = new Date();
        delivery.errorMessage = 'Mailbox full';
      }
    }, Math.random() * 30000); // 0-30 seconds delivery time
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private generateEmailId(): string {
    return `email_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private generateSequenceInstanceId(): string {
    return `seq_inst_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private createSuccessResponse(request: AgentAPIRequest, data: any, status: number, startTime: number): AgentAPIResponse {
    return {
      status,
      headers: {},
      data,
      duration: Date.now() - startTime,
      requestId: request.requestId,
      success: true,
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: {
          remaining: 999, // Mock rate limit
          resetTime: new Date(Date.now() + 60000)
        }
      }
    };
  }

  private createErrorResponse(
    request: AgentAPIRequest, 
    code: string, 
    message: string, 
    status: number, 
    startTime: number, 
    details?: any
  ): AgentAPIResponse {
    return {
      status,
      headers: {},
      data: null,
      duration: Date.now() - startTime,
      requestId: request.requestId,
      success: false,
      error: {
        code,
        message,
        details: {
          ...details,
          timestamp: new Date(),
          requestId: request.requestId,
          documentation: `https://docs.example.com/api/errors#${code.toLowerCase()}`
        }
      },
      metadata: {
        requestId: request.requestId,
        timestamp: new Date(),
        processingTime: Date.now() - startTime,
        rateLimit: {
          remaining: 999,
          resetTime: new Date(Date.now() + 60000)
        }
      }
    };
  }

  private initializeMockData(): void {
    // Create mock email templates
    const mockTemplates: EmailTemplate[] = [
      {
        id: 'template_welcome_1',
        name: 'Welcome Email',
        subject: 'Welcome to {{company_name}}!',
        htmlContent: '<h1>Welcome {{first_name}}!</h1><p>Thank you for joining {{company_name}}. We\'re excited to have you on board.</p>',
        textContent: 'Welcome {{first_name}}! Thank you for joining {{company_name}}. We\'re excited to have you on board.',
        category: 'welcome',
        variables: ['first_name', 'company_name'],
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01'),
        isActive: true
      },
      {
        id: 'template_promo_1',
        name: 'Product Promotion',
        subject: 'Special Offer: {{discount}}% Off {{product_name}}',
        htmlContent: '<h2>Limited Time Offer!</h2><p>Get {{discount}}% off {{product_name}}. Use code: {{promo_code}}</p>',
        textContent: 'Limited Time Offer! Get {{discount}}% off {{product_name}}. Use code: {{promo_code}}',
        category: 'promotional',
        variables: ['discount', 'product_name', 'promo_code'],
        createdAt: new Date('2024-01-05'),
        updatedAt: new Date('2024-01-10'),
        isActive: true
      },
      {
        id: 'template_newsletter_1',
        name: 'Monthly Newsletter',
        subject: '{{company_name}} Newsletter - {{month}} {{year}}',
        htmlContent: '<h1>{{company_name}} Newsletter</h1><p>Here are the latest updates from {{month}} {{year}}...</p>',
        textContent: '{{company_name}} Newsletter - Here are the latest updates from {{month}} {{year}}...',
        category: 'newsletter',
        variables: ['company_name', 'month', 'year'],
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-15'),
        isActive: true
      }
    ];

    mockTemplates.forEach(template => {
      this.emailTemplates.set(template.id, template);
    });

    // Create mock email sequences
    const mockSequences: EmailSequence[] = [
      {
        id: 'sequence_onboarding_1',
        name: 'User Onboarding Sequence',
        description: 'Welcome new users with a 3-email sequence',
        emails: [
          {
            id: 'seq_email_1',
            templateId: 'template_welcome_1',
            delayMinutes: 0
          },
          {
            id: 'seq_email_2',
            templateId: 'template_promo_1',
            delayMinutes: 1440 // 24 hours
          },
          {
            id: 'seq_email_3',
            templateId: 'template_newsletter_1',
            delayMinutes: 4320 // 72 hours
          }
        ],
        isActive: true,
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-01')
      }
    ];

    mockSequences.forEach(sequence => {
      this.emailSequences.set(sequence.id, sequence);
    });
  }
}

// Export singleton instance
export const agentEmailService = new AgentEmailService();